import Head from 'next/head';
import Link from 'next/link';
import { useState, useEffect } from 'react';

function TestimonialCard({ t }) {
    return (
        <div className="bg-white p-6 rounded-md border border-gray-100 shadow-sm hover:shadow-xl hover:shadow-brand-primary/10 transition-all duration-300 group text-left">
            <div className="flex gap-1 mb-4">
                {[...Array(t.rating)].map((_, j) => (
                    <svg key={j} className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                ))}
            </div>
            <p className="text-sm text-gray-700 mb-4 leading-relaxed italic">"{t.text}"</p>
            <div>
                <h5 className="font-bold text-brand-dark text-sm">{t.author}</h5>
                <p className="text-gray-500 text-xs">{t.role}</p>
            </div>
        </div>
    );
}

const impactStories = [
    {
        text: "Rahil is the finest training professional we've worked with in over a decade. He owns any room, connects instinctively from senior management to frontline staff, and delivers sharp, specific solutions. He doesn't just train — he aligns. Every module is built with intent, and every session is memorable. A trusted partner who raises the bar and over-delivers, every time.",
        author: "Platinum Guild International",
        role: "Industry Partner",
        rating: 5
    },
    {
        text: "We've seen a measurable shift in how our managers approach problem-solving and team inspiration after undergoing the L.I.S.T modules.",
        author: "Dubai Duty Free",
        role: "Retail Excellence Partner",
        rating: 5
    }
];

const getImpactCardsPerView = (width) => {
    if (width >= 1024) return 3;
    if (width >= 768) return 2;
    return 1;
};

export default function WhyChooseUs() {
    const [impactIndex, setImpactIndex] = useState(0);
    const [impactCardsPerView, setImpactCardsPerView] = useState(3);
    const differentiators = [
        {
            number: "01",
            title: "Preventative, Not Reactive",
            desc: "We solve leadership bottlenecks before they impact your brand's bottom line by building proactive mental habits.",
            icon: (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            )
        },
        {
            number: "02",
            title: "Luxury Retail Specific",
            desc: "Our curriculum isn't generic. It's built for the high-stakes, high-touch world of premium retail and hospitality.",
            icon: (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            )
        },
        {
            number: "03",
            title: "Experiential Learning",
            desc: "Move beyond theory with case studies, real-world simulations, and practical toolkits you can use tomorrow.",
            icon: (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            )
        },
        {
            number: "04",
            title: "Small Cohorts",
            desc: "We limit program sizes to ensure every participant receives personalized feedback and deep peer networking.",
            icon: (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            )
        },
        {
            number: "05",
            title: "Comfort Learning",
            desc: "The program offers training in multiple languages – English, Hindi, 8 regional languages, and Arabic.",
            icon: (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            )
        },
        {
            number: "06",
            title: "Practical Application",
            desc: "Every module ends with a tangible action plan. We measure success by your results, not just your attendance.",
            icon: (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            )
        }
    ];

    useEffect(() => {
        const updateImpactLayout = () => {
            const cardsPerView = getImpactCardsPerView(window.innerWidth);
            setImpactCardsPerView(cardsPerView);
            setImpactIndex((prev) => Math.min(prev, Math.max(impactStories.length - cardsPerView, 0)));
        };

        updateImpactLayout();
        window.addEventListener('resize', updateImpactLayout);

        return () => window.removeEventListener('resize', updateImpactLayout);
    }, []);

    const maxImpactIndex = Math.max(impactStories.length - impactCardsPerView, 0);
    const nextImpactSlide = () => setImpactIndex((prev) => Math.min(prev + 1, maxImpactIndex));
    const prevImpactSlide = () => setImpactIndex((prev) => Math.max(prev - 1, 0));

    return (
        <>
            <Head>
                <title>Why Choose Us | L.I.S.T Learning Pathways</title>
                <meta name="description" content="Discover what makes L.I.S.T the premier choice for leadership development in the luxury sector." />
            </Head>

            <main className="min-h-screen bg-white">
                {/* Hero */}
                <section className="relative overflow-hidden bg-brand-dark pt-20 pb-16 lg:pt-32 lg:pb-20">
                    <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/4 w-[400px] h-[400px] bg-brand-primary/10 rounded-full blur-3xl"></div>
                    <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
                        <span className="inline-block px-4 py-1.5 bg-brand-primary/20 text-white text-xs font-bold rounded-full mb-4">
                            The L.I.S.T Edge
                        </span>
                        <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-[1.1] tracking-tight">
                            The <span className="text-brand-primary italic">Differentiator</span>
                        </h1>
                        <p className="text-lg text-gray-300 max-w-2xl leading-relaxed">
                            In a world of generic management training, L.I.S.T stands apart through niche specialization and extreme focus on impact.
                        </p>
                    </div>
                </section>

                {/* Differentiators Grid */}
                <section className="py-16 lg:py-20 bg-white">
                    <div className="max-w-7xl mx-auto px-6 lg:px-12">
                        <div className="text-center mb-12">
                            <span className="text-brand-primary font-bold text-xs mb-3 block">Our Unique Edge</span>
                            <h3 className="text-3xl lg:text-4xl font-extrabold text-brand-dark tracking-tight">What Makes Us Different</h3>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
                            {differentiators.map((d, i) => (
                                <div key={i} className="group p-6 bg-white border border-gray-100 rounded-md shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                                    <div className="flex items-center gap-4 mb-4">
                                        <div className="w-12 h-12 rounded-lg bg-brand-primary/10 flex items-center justify-center group-hover:bg-brand-primary transition-colors shrink-0">
                                            <svg className="w-6 h-6 text-brand-primary group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                {d.icon}
                                            </svg>
                                        </div>
                                        <span className="text-2xl font-black text-brand-primary/20">{d.number}</span>
                                    </div>
                                    <h4 className="text-lg font-bold mb-3 text-brand-dark group-hover:text-brand-primary transition-colors">{d.title}</h4>
                                    <p className="text-gray-500 text-sm leading-relaxed">{d.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Methodology Stats */}
                <section className="py-16 lg:py-20 bg-gray-50/50">
                    <div className="max-w-7xl mx-auto px-6 lg:px-12">
                        <div className="lg:grid lg:grid-cols-2 gap-16 items-center max-w-5xl mx-auto">
                            <div className="aspect-[4/3] relative rounded-lg overflow-hidden shadow-xl order-2 lg:order-1 group">
                                <img
                                    src="/list-hero/4.jpg"
                                    alt="Success Analytics"
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-brand-dark/20 group-hover:bg-brand-dark/10 transition-colors"></div>
                            </div>
                            <div className="order-1 lg:order-2 mb-10 lg:mb-0">
                                <span className="text-brand-primary font-bold text-xs mb-3 block">Proven Methodology</span>
                                <h3 className="text-3xl md:text-4xl font-extrabold text-brand-dark mb-8 tracking-tight">Our Success Metrics</h3>
                                <div className="space-y-8">
                                    {[
                                        { label: "Internal Mobility Increase", val: "35%", color: "bg-brand-primary" },
                                        { label: "Team Performance Uplift", val: "22%", color: "bg-brand-primary" },
                                        { label: "Leadership Retention Rate", val: "94%", color: "bg-brand-primary" }
                                    ].map((stat, i) => (
                                        <div key={i}>
                                            <div className="flex justify-between items-end mb-3">
                                                <span className="text-sm font-bold text-gray-500">{stat.label}</span>
                                                <span className="text-2xl font-black text-brand-dark">{stat.val}</span>
                                            </div>
                                            <div className="w-full h-2 bg-white rounded-full overflow-hidden shadow-inner">
                                                <div className={`h-full ${stat.color} rounded-full`} style={{ width: stat.val }}></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Testimonials Section */}
                <section className="py-16 lg:py-20 bg-white">
                    <div className="max-w-7xl mx-auto px-6 lg:px-12">
                        <div className="text-center mb-12">
                            <span className="text-brand-primary font-bold text-xs mb-3 block">Impact Stories</span>
                            <h3 className="text-3xl lg:text-4xl font-extrabold text-brand-dark tracking-tight">What Our Alumni Say</h3>
                        </div>

                        <div className="flex justify-center gap-3 mb-8">
                            <button
                                type="button"
                                onClick={prevImpactSlide}
                                disabled={impactIndex === 0}
                                className="w-11 h-11 rounded-full border border-gray-200 bg-white text-brand-dark flex items-center justify-center shadow-sm transition-all hover:border-brand-primary hover:text-brand-primary disabled:opacity-40 disabled:cursor-not-allowed"
                                aria-label="Previous testimonial"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                                </svg>
                            </button>
                            <button
                                type="button"
                                onClick={nextImpactSlide}
                                disabled={impactIndex >= maxImpactIndex}
                                className="w-11 h-11 rounded-full border border-gray-200 bg-white text-brand-dark flex items-center justify-center shadow-sm transition-all hover:border-brand-primary hover:text-brand-primary disabled:opacity-40 disabled:cursor-not-allowed"
                                aria-label="Next testimonial"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                                </svg>
                            </button>
                        </div>

                        <div className="overflow-hidden max-w-5xl mx-auto">
                            <div
                                className="flex transition-transform duration-500 ease-out"
                                style={{
                                    transform: `translateX(-${impactIndex * (100 / impactCardsPerView)}%)`,
                                }}
                            >
                                {impactStories.map((t, i) => (
                                    <div
                                        key={i}
                                        className="shrink-0 px-0 md:px-3"
                                        style={{ width: `${100 / impactCardsPerView}%` }}
                                    >
                                        <TestimonialCard t={t} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Closing CTA */}
                <section className="py-16 lg:py-20 bg-white">
                    <div className="max-w-7xl mx-auto px-6 lg:px-12">
                        <div className="bg-brand-dark rounded-lg p-8 lg:p-12 text-center text-white relative overflow-hidden shadow-xl">
                            <div className="absolute inset-0 bg-brand-primary/10"></div>
                            <div className="relative z-10 max-w-2xl mx-auto">
                                <h3 className="text-2xl md:text-3xl font-extrabold mb-4 tracking-tight">Choose Mastery <span className="text-brand-primary">Above All</span>.</h3>
                                <p className="text-base text-gray-300 mb-8">Join the ranks of influential leaders who have transformed their careers through the L.I.S.T framework.</p>
                                <Link href="/enrollment" className="inline-block px-8 py-4 bg-brand-primary text-white font-bold rounded-md shadow-lg shadow-brand-primary/20 hover:shadow-brand-primary/40 transition-all active:scale-95">
                                    Begin Your Enrollment
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
        </>
    );
}
